import React, { useState } from 'react';
import axios from '../api/Axios';

const StudentRegisterForm = () => {
  const [formData, setFormData] = useState({
    title: '',
    lastName: '',
    otherNames: '',
    gender: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobilityImpaired: false
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    
    if (!/\d/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push("Password must contain at least one special character");
    }
    
    return errors;
  };

  // Calculate password strength score
  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score++;
    return score;
  };

  // Get password strength label and color
  const getPasswordStrengthInfo = (score) => {
    switch (score) {
      case 0:
      case 1:
        return { label: 'Very Weak', color: '#dc3545', width: '20%' };
      case 2:
        return { label: 'Weak', color: '#fd7e14', width: '40%' };
      case 3:
        return { label: 'Fair', color: '#ffc107', width: '60%' };
      case 4:
        return { label: 'Good', color: '#20c997', width: '80%' };
      case 5:
        return { label: 'Strong', color: '#198754', width: '100%' };
      default:
        return { label: '', color: '', width: '0%' };
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Validate password in real-time
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    if (formData.password !== formData.confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    // Validate password strength
    const passwordValidationErrors = validatePassword(formData.password);
    if (passwordValidationErrors.length > 0) {
      setErrorMsg("Please fix the password requirements:\n" + passwordValidationErrors.join('\n'));
      return;
    }

    setLoading(true);

    // Compose payload for Django API
    const payload = {
      user: {
        email: formData.email,
        password: formData.password,
        // user_type is set server-side
      },
      campus: null,
      institution: null,
      title: formData.title,
      surname: formData.lastName,
      other_names: formData.otherNames,
      nationality: "",
      id_document_type: "",
      id_number: "",
      phone_number: "",
      gender: formData.gender,
      course: "",
      year_of_study: "",
      wheel_chair_req: formData.mobilityImpaired
    };

    try {
      await axios.post("accounts/register/student/", payload);
      setSuccessMsg("Registration successful! Please check your email to verify your account before logging in.");
      setFormData({
        title: '',
        lastName: '',
        otherNames: '',
        gender: '',
        email: '',
        password: '',
        confirmPassword: '',
        mobilityImpaired: false
      });
    } catch (err) {
      console.log("Full error object:", err);
      console.log("Error response data:", err.response?.data);
      
      if (err.response?.data) {
        // Convert the nested error object to a user-friendly string
        const formatErrors = (errorObj) => {
          const messages = [];
          
          const extractMessages = (obj, prefix = '') => {
            if (Array.isArray(obj)) {
              obj.forEach(msg => messages.push(prefix + msg));
            } else if (typeof obj === 'object' && obj !== null) {
              Object.entries(obj).forEach(([key, value]) => {
                const newPrefix = prefix ? `${prefix} > ${key}: ` : `${key}: `;
                extractMessages(value, newPrefix);
              });
            } else {
              messages.push(prefix + String(obj));
            }
          };
          
          extractMessages(errorObj);
          return messages.join('\n');
        };
        
        setErrorMsg(formatErrors(err.response.data));
      } else {
        setErrorMsg("Submission failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.17)',
        color: 'rgba(255, 255, 255)',
        padding: '2rem',
        margin: '1rem 0',
        boxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
        WebkitBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
        MozBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)'
    }}>
        <form onSubmit={handleSubmit}>
            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">Titles</label>
                        <input
                            type="text"
                            className="form-control"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Title"
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">Last Name</label>
                        <input
                            type="text"
                            className="form-control"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Last Name"
                        />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">Other Names</label>
                        <input
                            type="text"
                            className="form-control"
                            name="otherNames"
                            value={formData.otherNames}
                            onChange={handleChange}
                            placeholder="Other Names"
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">Gender</label>
                        <select
                            className="form-control"
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-12">
                    <div className="mb-3">
                        <label className="form-label text-white">Email</label>
                        <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Email"
                        />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">Password</label>
                        <input
                            type="password"
                            className="form-control"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Password"
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">Confirm Password</label>
                        <input
                            type="password"
                            className="form-control"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm Password"
                        />
                    </div>
                </div>
            </div>

            {/* Password Requirements - Full Width */}
            {formData.password && (
                <div className="alert alert-info mb-3" style={{ fontSize: '0.9rem' }}>
                    {/* Password Strength Meter */}
                    <div className="mb-2">
                        <small>Password Strength: </small>
                        <span style={{ color: getPasswordStrengthInfo(getPasswordStrength(formData.password)).color, fontWeight: 'bold' }}>
                            {getPasswordStrengthInfo(getPasswordStrength(formData.password)).label}
                        </span>
                        <div className="progress mt-1" style={{ height: '4px' }}>
                            <div 
                                className="progress-bar" 
                                style={{ 
                                    width: getPasswordStrengthInfo(getPasswordStrength(formData.password)).width,
                                    backgroundColor: getPasswordStrengthInfo(getPasswordStrength(formData.password)).color
                                }}
                            ></div>
                        </div>
                    </div>
                    
                    <small>Password Requirements:</small>
                    <ul className="list-unstyled mt-1" style={{ fontSize: '0.85rem' }}>
                        <li className={formData.password.length >= 8 ? 'text-success' : 'text-danger'}>
                            <span style={{ 
                                marginRight: '5px',
                                ...(formData.password.length >= 8 
                                    ? { color: '#198754', fontWeight: 'bold' }
                                    : { color: '#dc3545', fontWeight: 'bold' }
                                )
                            }}>
                                {formData.password.length >= 8 ? '✓' : '○'}
                            </span>
                            At least 8 characters
                        </li>
                        <li className={/[A-Z]/.test(formData.password) ? 'text-success' : 'text-danger'}>
                            <span style={{ 
                                marginRight: '5px',
                                ...(/[A-Z]/.test(formData.password) 
                                    ? { color: '#198754', fontWeight: 'bold' }
                                    : { color: '#dc3545', fontWeight: 'bold' }
                                )
                            }}>
                                {/[A-Z]/.test(formData.password) ? '✓' : '○'}
                            </span>
                            One uppercase letter
                        </li>
                        <li className={/[a-z]/.test(formData.password) ? 'text-success' : 'text-danger'}>
                            <span style={{ 
                                marginRight: '5px',
                                ...(/[a-z]/.test(formData.password) 
                                    ? { color: '#198754', fontWeight: 'bold' }
                                    : { color: '#dc3545', fontWeight: 'bold' }
                                )
                            }}>
                                {/[a-z]/.test(formData.password) ? '✓' : '○'}
                            </span>
                            One lowercase letter
                        </li>
                        <li className={/\d/.test(formData.password) ? 'text-success' : 'text-danger'}>
                            <span style={{ 
                                marginRight: '5px',
                                ...(/\d/.test(formData.password) 
                                    ? { color: '#198754', fontWeight: 'bold' }
                                    : { color: '#dc3545', fontWeight: 'bold' }
                                )
                            }}>
                                {/\d/.test(formData.password) ? '✓' : '○'}
                            </span>
                            One number
                        </li>
                        <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password) ? 'text-success' : 'text-danger'}>
                            <span style={{ 
                                marginRight: '5px',
                                ...(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password) 
                                    ? { color: '#198754', fontWeight: 'bold' }
                                    : { color: '#dc3545', fontWeight: 'bold' }
                                )
                            }}>
                                {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password) ? '✓' : '○'}
                            </span>
                            One special character (!@#$%^&* etc.)
                        </li>
                    </ul>
                </div>
            )}

            {/* Password Match Indicator - Full Width */}
            {formData.confirmPassword && (
                <div className="alert alert-info mb-3" style={{ fontSize: '0.9rem', padding: '0.5rem' }}>
                    <small className={formData.password === formData.confirmPassword ? 'text-success' : 'text-danger'}>
                        <span style={{
                            marginRight: '5px',
                            fontWeight: 'bold',
                            ...(formData.password === formData.confirmPassword 
                                ? { color: '#198754' }
                                : { color: '#dc3545' }
                            )
                        }}>
                            {formData.password === formData.confirmPassword ? '✓' : '✗'}
                        </span>
                        {formData.password === formData.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </small>
                </div>
            )}

            <div className="mb-3 form-check">
                <input
                    type="checkbox"
                    className="form-check-input"
                    id="mobilityImpaired"
                    name="mobilityImpaired"
                    checked={formData.mobilityImpaired}
                    onChange={handleChange}
                />
                <label className="form-check-label text-white" htmlFor="mobilityImpaired">
                    Require Wheel Chair Accessible Accomodations
                </label>
            </div>

            {errorMsg && (
                <div className="alert alert-danger mt-2">
                    {errorMsg.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                    ))}
                </div>
            )}
            
            {successMsg && (
                <div className="alert alert-success mt-2">{successMsg}</div>
            )}
            
            <button
                type="submit"
                className="btn btn-primary w-100"
                style={{
                    backgroundColor: '#0d6efd',
                    borderColor: '#0d6efd',
                    padding: '10px 30px'
                }}
                disabled={loading || passwordErrors.length > 0 || !formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword}
            >
                {loading ? "Submitting..." : "Submit"}
            </button>
        </form>
    </div>
  );
};

export default StudentRegisterForm;